import React, { useEffect, useMemo, useRef, useState } from "react";
import { Popover, UnstyledButton, ActionIcon, Text, Flex } from "@mantine/core";
import { CustomVerticalScroll } from "@components/custom/table/custom-vertical-scroll";
import styles from "@scss/custom-components/form-fields-dropdowns.module.scss";

export type CustomTimePopoverProps = {
  value: Date | null | undefined;
  onChange?: (v: Date | null) => void;
  step?: number; // minutes between items
  className?: string;
  ariaLabel?: string;
  variant?: "default" | "dark" | "darker" | "sky" | string;
  withinPortal?: boolean;
  maxH?: number | string;
  maxMin?: number | string;
  /** optional custom target element - if provided, the popover will use this element as the click target (wrapping it in a clickable div) */
  target?: React.ReactNode;
};

function pad2(n: number) {
  return n.toString().padStart(2, "0");
}

export const CustomTimePopover: React.FC<CustomTimePopoverProps> = ({
  value,
  onChange,
  className,
  ariaLabel,
  variant = "default",
  withinPortal,
  maxH,
  maxMin,
  step,
  target,
}) => {
  const [opened, setOpened] = useState(false);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const pickedHour = useRef(false);
  const pickedMinute = useRef(false);
  // parse and clamp max values passed from parent; default to 23 and 59
  const maxHour = useMemo(() => {
    const maybe = maxH ?? 23;
    const n =
      typeof maybe === "string" ? parseInt(maybe, 10) : (maybe as number);
    if (Number.isNaN(n)) return 23;
    return Math.max(0, Math.min(23, Math.floor(n)));
  }, [maxH]);

  const maxMinute = useMemo(() => {
    const maybe = maxMin ?? 59;
    const n =
      typeof maybe === "string" ? parseInt(maybe, 10) : (maybe as number);
    if (Number.isNaN(n)) return 59;
    return Math.max(0, Math.min(59, Math.floor(n)));
  }, [maxMin]);

  // always show full 0-23 hours, but we will disable those beyond maxHour
  const hours = useMemo(
    () => Array.from({ length: 24 }, (_, i) => pad2(i)),
    [],
  );

  // numeric selected hour (used to decide whether to clamp minutes)
  const selectedHourNum = value ? value.getHours() : undefined;

  // always show full 0-59 minutes, but disable minutes beyond maxMinute when selected hour === maxHour
  const minutes = useMemo(
    () => Array.from({ length: 60 }, (_, i) => pad2(i)),
    [],
  );

  const selectedHour = value ? pad2(value.getHours()) : undefined;
  const selectedMinute = value ? pad2(value.getMinutes()) : undefined;

  const handleHourSelect = (hh: string) => {
    if (!onChange) return;
    // eslint-disable-next-line no-console
    console.debug("[CustomTimePopover] handleHourSelect", hh, value);
    pickedHour.current = true;
    const h = parseInt(hh, 10);
    const next = value ? new Date(value) : new Date();
    if (value) {
      next.setHours(h);
    } else {
      next.setHours(h, 0, 0, 0);
    }
    onChange(next);
    if (pickedHour.current && pickedMinute.current) {
      setTimeout(() => setOpened(false), 50);
    }
  };

  const handleMinuteSelect = (mm: string) => {
    if (!onChange) return;
    // eslint-disable-next-line no-console
    console.debug("[CustomTimePopover] handleMinuteSelect", mm, value);
    pickedMinute.current = true;
    const m = parseInt(mm, 10);
    const next = value ? new Date(value) : new Date();
    next.setMinutes(m);
    onChange(next);
    if (pickedHour.current && pickedMinute.current) {
      setTimeout(() => setOpened(false), 50);
    }
  };

  useEffect(() => {
    if (!opened) return;
    // reset session picks whenever popover opens
    pickedHour.current = false;
    pickedMinute.current = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const handler = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      // delay closing so any internal click handlers can run first
      timer = setTimeout(() => setOpened(false), 0);
    };
    // use mouseup/touchend so clicks inside dropdown finish before outside handler runs
    document.addEventListener("mouseup", handler);
    document.addEventListener("touchend", handler);
    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener("mouseup", handler);
      document.removeEventListener("touchend", handler);
    };
  }, [opened]);
  const variantDropdownClass =
    (styles as any)[`timeDropdown--${variant}`] || "";

  return (
    <Popover
      opened={opened}
      onClose={() => setOpened(false)}
      position="bottom"
      withArrow={false}
      trapFocus={false}
      withinPortal={withinPortal}
    >
      <Popover.Target>
        {target ? (
          <div
            ref={triggerRef}
            className={className}
            onClick={() => setOpened((v) => !v)}
            // allow clicks inside the target to open the popover
          >
            {target}
          </div>
        ) : (
          <div ref={triggerRef} className={className}>
            <ActionIcon
              size="xs"
              aria-label={ariaLabel ?? "Open time picker"}
              onClick={() => setOpened((v) => !v)}
              className={styles.clockButton}
            >
              <></>
            </ActionIcon>
          </div>
        )}
      </Popover.Target>

      <Popover.Dropdown
        className={`${styles.timePickerDropdown} ${variantDropdownClass}`}
      >
        <div ref={dropdownRef}>
          <Flex>
            <div>
              <CustomVerticalScroll height={200} fadeHeight={25}>
                {hours.map((hh) => {
                  const hourNum = parseInt(hh, 10);
                  const isDisabled = hourNum > maxHour;
                  return (
                    <UnstyledButton
                      key={hh}
                      disabled={isDisabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (isDisabled) return;
                        handleHourSelect(hh);
                      }}
                      className={`${styles.options} ${hh === selectedHour ? styles.optionsSelected : ""} ${isDisabled ? (styles as any).optionsDisabled : ""}`}
                    >
                      <Text size="sm">{hh}</Text>
                    </UnstyledButton>
                  );
                })}
              </CustomVerticalScroll>
            </div>

            <div>
              <CustomVerticalScroll height={200} fadeHeight={25}>
                {minutes.map((mm) => {
                  const minuteNum = parseInt(mm, 10);
                  const isDisabled =
                    selectedHourNum !== undefined &&
                    selectedHourNum === maxHour &&
                    minuteNum > maxMinute;
                  return (
                    <UnstyledButton
                      key={mm}
                      disabled={isDisabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (isDisabled) return;
                        handleMinuteSelect(mm);
                      }}
                      className={`${styles.options} ${mm === selectedMinute ? styles.optionsSelected : ""} ${isDisabled ? (styles as any).optionsDisabled : ""}`}
                    >
                      <Text size="sm">{mm}</Text>
                    </UnstyledButton>
                  );
                })}
              </CustomVerticalScroll>
            </div>
          </Flex>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
};

export default CustomTimePopover;
